import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api.service';

/**
 * Custom hook for fetching and managing broadcasts with pagination and sorting
 */
export const useBroadcasts = ({ search = '' } = {}) => {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [totalRows, setTotalRows] = useState(0);
	const [perPage, setPerPage] = useState(10);
	const [sort, setSort] = useState('createdAt');
	const [sortDirection, setSortDirection] = useState('desc');

	// Fetch broadcasts
	const fetchBroadcasts = useCallback(async (page = 1) => {
		setLoading(true);
		setError(null);

		try {
			const response = await apiService.broadcasts.getAll({
				page,
				perPage,
				sort,
				sortDirection,
				search
			});

			setData(response.data.data);
			setTotalRows(response.data.total);
		} catch (err) {
			setError(err.message || 'Failed to fetch broadcasts');
			console.error('Error fetching broadcasts:', err);
		} finally {
			setLoading(false);
		}
	}, [perPage, sort, sortDirection, search]);

	// Handle page change
	const handlePageChange = useCallback((page) => {
		fetchBroadcasts(page);
	}, [fetchBroadcasts]);

	// Handle sort change
	const handleSort = useCallback(async (column, newSortDirection) => {
		setSort(column.sortField);
		setSortDirection(newSortDirection);
		setLoading(true);

		try {
			const response = await apiService.broadcasts.getAll({
				page: 1,
				perPage,
				sort: column.sortField,
				sortDirection: newSortDirection,
				search
			});

			setData(response.data.data);
		} catch (err) {
			setError(err.message || 'Failed to sort broadcasts');
			console.error('Error sorting broadcasts:', err);
		} finally {
			setLoading(false);
		}
	}, [perPage, search]);

	// Handle per page change
	const handlePerRowsChange = useCallback(async (newPerPage, page) => {
		setPerPage(newPerPage);
		setLoading(true);

		try {
			const response = await apiService.broadcasts.getAll({
				page,
				perPage: newPerPage,
				sort,
				sortDirection,
				search
			});

			setData(response.data.data);
		} catch (err) {
			setError(err.message || 'Failed to update page size');
			console.error('Error changing page size:', err);
		} finally {
			setLoading(false);
		}
	}, [sort, sortDirection, search]);

	// Delete broadcast
	const deleteBroadcast = useCallback(async (id) => {
		try {
			await apiService.broadcasts.delete(id);
			// Refetch current page
			await fetchBroadcasts(1);
			return { success: true };
		} catch (err) {
			setError(err.message || 'Failed to delete broadcast');
			console.error('Error deleting broadcast:', err);
			return { success: false, error: err.message };
		}
	}, [fetchBroadcasts]);

	// Fetch on mount and when search changes
	useEffect(() => {
		fetchBroadcasts(1);
	}, [search]);

	return {
		data,
		loading,
		error,
		totalRows,
		perPage,
		sort,
		sortDirection,
		handlePageChange,
		handleSort,
		handlePerRowsChange,
		deleteBroadcast,
		refetch: fetchBroadcasts
	};
};
